<script setup lang="ts">
import {
  ArrowRightIcon,
  AtSignIcon,
  BookOpenIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  PaperclipIcon,
  PlayCircleIcon,
  SearchIcon,
  UsersIcon,
} from "lucide-vue-next";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

interface Props {
  organizationName?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  dismiss: [];
}>();

const appName = computed(() => props.organizationName?.trim() || "Org Demo");
const appIconSrc = "/icon.png";
const MOBILE_VIDEO_MEDIA_QUERY = "(max-width: 639px)";

const step = ref<"read" | "video">("read");
const isMobileVideo = ref<boolean | null>(
  typeof window === "undefined"
    ? null
    : window.matchMedia(MOBILE_VIDEO_MEDIA_QUERY).matches,
);
let mobileVideoQuery: MediaQueryList | null = null;

const syncMobileVideo = (event?: MediaQueryListEvent) => {
  isMobileVideo.value = event?.matches ?? mobileVideoQuery?.matches ?? false;
};

onMounted(() => {
  if (typeof window === "undefined") {
    return;
  }

  mobileVideoQuery = window.matchMedia(MOBILE_VIDEO_MEDIA_QUERY);
  syncMobileVideo();
  mobileVideoQuery.addEventListener("change", syncMobileVideo);
});

onBeforeUnmount(() => {
  mobileVideoQuery?.removeEventListener("change", syncMobileVideo);
});

const goToVideo = () => {
  step.value = "video";
};

const dismiss = () => {
  emit("dismiss");
};
</script>

<template>
  <div
    class="flex min-h-0 flex-1 items-start justify-center overflow-y-auto p-3 sm:p-4"
  >
    <!-- Step 1: Written guide (original content) -->
    <div v-if="step === 'read'" class="w-full max-w-5xl">
      <div class="mb-8 text-center">
        <div
          class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-background"
        >
          <img
            alt="Condor"
            class="h-full w-full rounded-2xl object-cover"
            :src="appIconSrc"
          />
        </div>
        <h1 class="mb-2 text-xl font-semibold tracking-tight text-foreground">
          Bienvenido a {{ appName }}
        </h1>
        <p
          class="mx-auto max-w-md text-[13px] leading-relaxed text-muted-foreground"
        >
          Tu espacio para comunicarte con tu equipo de forma organizada. Acá va
          un resumen rápido de cómo funciona.
        </p>
      </div>

      <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div class="rounded-2xl border border-border bg-background p-5">
          <div
            class="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"
          >
            <MessageSquareIcon class="h-4 w-4 text-primary" />
          </div>
          <h3 class="mb-1.5 text-[13px] font-semibold text-foreground">
            Hilos
          </h3>
          <p class="mb-3 text-[12px] leading-relaxed text-muted-foreground">
            Cada conversación es un hilo con un tema específico. Respondé,
            mencioná personas y adjuntá archivos.
          </p>
          <div class="space-y-1.5">
            <div class="flex items-center gap-2">
              <AtSignIcon class="h-3 w-3 text-primary/60" />
              <span class="text-[11px] text-muted-foreground/70"
                >Mencioná con @nombre</span
              >
            </div>
            <div class="flex items-center gap-2">
              <PaperclipIcon class="h-3 w-3 text-primary/60" />
              <span class="text-[11px] text-muted-foreground/70"
                >Adjuntá archivos e imágenes</span
              >
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-border bg-background p-5">
          <div
            class="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"
          >
            <BookOpenIcon class="h-4 w-4 text-primary" />
          </div>
          <h3 class="mb-1.5 text-[13px] font-semibold text-foreground">
            Directorio
          </h3>
          <p class="mb-3 text-[12px] leading-relaxed text-muted-foreground">
            ¿No sabés con quién hablar? Buscá por cargo, área o habilidad y
            encontrá a la persona indicada.
          </p>
          <div class="space-y-1.5">
            <div class="flex items-center gap-2">
              <SearchIcon class="h-3 w-3 text-primary/60" />
              <span class="text-[11px] text-muted-foreground/70"
                >Buscá por lo que necesitás</span
              >
            </div>
            <div class="flex items-center gap-2">
              <span
                class="flex h-3 w-3 items-center justify-center text-[8px] font-bold text-primary/60"
              >
                👤
              </span>
              <span class="text-[11px] text-muted-foreground/70"
                >Perfiles con bio y expertise</span
              >
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-border bg-background p-5">
          <div
            class="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"
          >
            <UsersIcon class="h-4 w-4 text-primary" />
          </div>
          <h3 class="mb-1.5 text-[13px] font-semibold text-foreground">
            Equipos
          </h3>
          <p class="mb-3 text-[12px] leading-relaxed text-muted-foreground">
            Los equipos agrupan personas por área. Mencioná un @equipo para
            notificar a todos sus miembros de una vez.
          </p>
          <div class="space-y-1.5">
            <div class="flex items-center gap-2">
              <span class="text-[11px] font-medium text-primary/60">@</span>
              <span class="text-[11px] text-muted-foreground/70"
                >@comerciales, @finanzas...</span
              >
            </div>
            <div class="flex items-center gap-2">
              <UsersIcon class="h-3 w-3 text-primary/60" />
              <span class="text-[11px] text-muted-foreground/70"
                >Creá hilos con equipos enteros</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="mb-5 rounded-2xl border border-border bg-background p-5">
        <div class="mb-3 flex items-center gap-2">
          <MessageCircleIcon class="h-3.5 w-3.5 text-primary/60" />
          <h3
            class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/60"
          >
            Ejemplo de un hilo
          </h3>
        </div>

        <div class="mb-3 rounded-lg border border-border p-3">
          <div class="mb-1.5 flex items-center gap-2">
            <div
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10"
            >
              <MessageCircleIcon class="h-3 w-3 text-primary" />
            </div>
            <span class="text-[13px] font-semibold text-foreground"
              >Revisión propuesta cliente ABC</span
            >
          </div>
          <p class="ml-8 text-[12px] text-muted-foreground">
            <span
              class="mr-1 inline-flex items-center rounded bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary"
            >
              @comerciales
            </span>
            necesito que revisen la propuesta antes del viernes
          </p>
        </div>

        <div class="ml-1 space-y-2.5">
          <div class="flex gap-2.5">
            <div
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10"
            >
              <span class="text-[8px] font-semibold text-primary">JD</span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5">
                <span class="text-[12px] font-semibold text-foreground"
                  >Juan Demo</span
                >
                <span class="text-[10px] text-muted-foreground/50"
                  >hace 2h</span
                >
              </div>
              <p class="text-[12px] leading-relaxed text-foreground/70">
                Ya la revisé, los números están bien. Solo ajustaría el plazo de
                entrega.
              </p>
            </div>
          </div>

          <div class="flex gap-2.5">
            <div
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10"
            >
              <span class="text-[8px] font-semibold text-primary">ML</span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5">
                <span class="text-[12px] font-semibold text-foreground"
                  >María López</span
                >
                <span class="text-[10px] text-muted-foreground/50"
                  >hace 1h</span
                >
              </div>
              <p class="text-[12px] leading-relaxed text-foreground/70">
                <span
                  class="mr-0.5 inline-flex items-center rounded bg-primary/10 px-1 py-px text-[10px] font-semibold text-primary"
                >
                  @Juan Demo
                </span>
                dale, ajusto los costos y te paso el archivo actualizado 📎
              </p>
            </div>
          </div>

          <div class="flex gap-2.5">
            <div
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10"
            >
              <span class="text-[8px] font-semibold text-primary">AM</span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5">
                <span class="text-[12px] font-semibold text-foreground"
                  >Ana Martínez</span
                >
                <span class="text-[10px] text-muted-foreground/50"
                  >hace 30min</span
                >
              </div>
              <p class="mb-1.5 text-[12px] leading-relaxed text-foreground/70">
                Listo, agendé la reunión de seguimiento para el lunes.
              </p>
              <div
                class="inline-flex items-center gap-2 rounded-lg border border-border bg-primary/[0.04] px-2.5 py-1.5"
              >
                <PaperclipIcon class="h-3 w-3 text-primary/60" />
                <span class="text-[10px] font-medium text-muted-foreground"
                  >agenda-reunion.pdf</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="mb-6 rounded-2xl border border-border bg-background px-5 py-4"
      >
        <div class="flex items-start gap-3">
          <div
            class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10"
          >
            <span class="text-sm">💡</span>
          </div>
          <div>
            <p class="mb-0.5 text-[13px] font-medium text-foreground">
              Consejo: completá tu perfil
            </p>
            <p class="text-[12px] leading-relaxed text-muted-foreground">
              Andá a
              <span class="font-medium text-foreground">Ajustes → Perfil</span>
              y completá tu cargo, bio y en qué podés ayudar. Así tu equipo sabe
              cuándo contactarte.
            </p>
          </div>
        </div>
      </div>

      <div class="text-center">
        <button
          class="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          type="button"
          @click="goToVideo"
        >
          Continuar
          <PlayCircleIcon class="h-4 w-4" />
        </button>
        <p class="mt-3 text-[11px] text-muted-foreground/50">
          A continuación verás un video con la guía paso a paso.
        </p>
      </div>
    </div>

    <!-- Step 2: Video guide -->
    <div
      v-else
      class="flex w-full max-w-3xl flex-col items-center justify-center"
    >
      <div class="mb-6 text-center">
        <h2
          class="mb-1 text-lg font-semibold tracking-tight text-foreground sm:text-xl"
        >
          Guía en video
        </h2>
        <p
          class="mx-auto max-w-sm text-[13px] leading-relaxed text-muted-foreground"
        >
          Mirá cómo crear hilos, mencionar personas y gestionar tus
          conversaciones.
        </p>
      </div>

      <div
        class="mx-auto mb-6 w-full overflow-hidden rounded-2xl border border-border bg-black shadow-sm"
      >
        <video
          v-if="isMobileVideo === false"
          autoplay
          muted
          playsinline
          controls
          class="w-full"
          style="aspect-ratio: 16 / 9"
        >
          <source :src="'/welcome-guide.mp4'" type="video/mp4" />
        </video>
        <video
          v-else-if="isMobileVideo === true"
          autoplay
          muted
          playsinline
          controls
          class="w-full"
          style="aspect-ratio: 9 / 16"
        >
          <source :src="'/welcome-guide-mobile.mp4'" type="video/mp4" />
        </video>
        <div v-else class="w-full bg-black" style="aspect-ratio: 16 / 9"></div>
      </div>

      <div class="text-center">
        <button
          class="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          type="button"
          @click="dismiss"
        >
          Empezar a usar la app
          <ArrowRightIcon class="h-4 w-4" />
        </button>
        <p class="mt-3 text-[11px] text-muted-foreground/50">
          Podés volver a ver esta guía desde Ajustes.
        </p>
      </div>
    </div>
  </div>
</template>
